<template>
  <div class="registration-brand-dashboard" v-loading="loading">
    <div class="dashboard-header">
      <div>
        <p>{{ activeBrand.brand || '--' }} · {{ year }} 年</p>
      </div>
      <div class="header-tools">
        <el-date-picker
          v-model="year"
          class="year-picker"
          type="year"
          size="small"
          value-format="yyyy"
          :clearable="false"
          placeholder="选择年份"
          @change="fetchDashboard"
        ></el-date-picker>
        <el-button
          type="primary"
          icon="el-icon-refresh"
          size="small"
          :loading="loading"
          @click="fetchDashboard"
        >刷新</el-button>
      </div>
    </div>

    <div v-if="shouldShowBrandSwitcher" class="brand-switcher">
      <button
        v-for="item in brandList"
        :key="item.id"
        type="button"
        class="brand-tab"
        :class="{ active: item.id === activeBrandId }"
        @click="selectBrand(item.id)"
      >
        <span class="brand-logo">
          <img v-if="item.logo" :src="item.logo" :alt="item.brand">
          <span v-else>{{ item.brand.slice(0, 1) }}</span>
        </span>
        <span class="brand-name">{{ item.brand }}</span>
      </button>
    </div>
    <div v-if="canUseCustomBrandKeyword" class="custom-brand-bar">
      <el-input
        v-model.trim="customBrandKeyword"
        class="custom-brand-input"
        size="small"
        clearable
        placeholder="输入自定义品牌关键词"
        @keyup.enter.native="selectCustomBrand"
      >
        <el-button
          slot="append"
          icon="el-icon-search"
          @click="selectCustomBrand"
        >统计</el-button>
      </el-input>
      <el-tag
        v-if="isCustomBrandActive"
        size="small"
        effect="plain"
        closable
        @close="clearCustomBrand"
      >自定义：{{ activeCustomKeyword }}</el-tag>
    </div>

    <div class="metric-grid">
      <div class="metric-card">
        <div class="metric-icon">
          <i class="el-icon-trophy"></i>
        </div>
        <div>
          <div class="metric-label">剩余赛事数量</div>
          <div class="metric-value">{{ dashboard.remainingEventCount }}</div>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon metric-icon-orders">
          <i class="el-icon-s-order"></i>
        </div>
        <div>
          <div class="metric-label">待完赛报名订单数量</div>
          <div class="metric-value">{{ dashboard.currentRegistrationCount }}</div>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon metric-icon-payment">
          <i class="el-icon-money"></i>
        </div>
        <div>
          <div class="metric-label">今年收款总额</div>
          <div class="metric-value">{{ formatMoney(dashboard.yearPaymentAmount) }}</div>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon metric-icon-sharing">
          <i class="el-icon-pie-chart"></i>
        </div>
        <div>
          <div class="metric-label">今年分账金额</div>
          <div class="metric-value">{{ formatMoney(dashboard.yearProfitSharingAmount) }}</div>
          <div class="metric-subtitle">含主订单及兼项订单</div>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon metric-icon-ratio">
          <i class="el-icon-data-analysis"></i>
        </div>
        <div>
          <div class="metric-label">分账占比</div>
          <div class="metric-value">{{ formatPercent(dashboard.profitSharingRatio) }}</div>
        </div>
      </div>
    </div>

    <section class="table-panel">
      <div class="panel-title">
        <div>
          <h3>未完赛赛事报名统计</h3>
          <span>按报名人数排序</span>
        </div>
        <el-tag size="small" effect="plain">共 {{ dashboard.eventStats.length }} 场</el-tag>
      </div>
      <el-table
        class="brand-event-table"
        :data="dashboard.eventStats"
        size="small"
        empty-text="暂无未完赛赛事数据"
      >
        <el-table-column prop="eventName" label="赛事名称" :min-width="isMobile ? 190 : 240" class-name="event-name-column">
          <template slot-scope="scope">
            <div class="event-name-actions">
              <copy-text :value="scope.row.eventName"></copy-text>
              <el-button
                v-if="canExportRegistration"
                type="text"
                size="mini"
                icon="el-icon-download"
                :loading="exportingEventId === scope.row.eventId"
                @click="exportEventRegistrations(scope.row)"
              >导出名单</el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="比赛时间" :width="isMobile ? 112 : 150">
          <template slot-scope="scope">{{ formatEventDate(scope.row) }}</template>
        </el-table-column>
        <el-table-column prop="registrationCount" label="报名人数" width="95" align="right"></el-table-column>
        <el-table-column label="收款总金额" width="130" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.totalPaymentAmount) }}</template>
        </el-table-column>
        <el-table-column label="分账总金额（含兼项）" width="155" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.totalProfitSharingAmount) }}</template>
        </el-table-column>
        <el-table-column label="分账占比" width="105" align="right">
          <template slot-scope="scope">{{ formatPercent(scope.row.profitSharingRatio) }}</template>
        </el-table-column>
        <el-table-column label="平均分账单价" width="130" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.averageProfitSharingUnit) }}</template>
        </el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script>
import { REGISTRATION_DASHBOARD_BRANDS } from '@/views/components/constant.js';
import { getCurrentUser, hasPermission } from '@/utils/auth';
import { exportRegistrationRows } from '@/utils/registrationExport';

const emptyDashboard = () => ({
  brand: {},
  year: '',
  timezone: 'Asia/Shanghai',
  yearStartAt: '',
  yearEndAt: '',
  remainingEventCount: 0,
  currentRegistrationCount: 0,
  yearPaymentAmount: 0,
  yearProfitSharingAmount: 0,
  profitSharingRatio: 0,
  eventStats: []
});

export default {
  name: 'RegistrationBrandDashboard',
  data() {
    return {
      loading: false,
      activeBrandId: REGISTRATION_DASHBOARD_BRANDS[0]?.id || '',
      customBrandKeyword: '',
      activeCustomKeyword: '',
      year: this.$dayjs().format('YYYY'),
      dashboard: emptyDashboard(),
      isMobile: false,
      exportingEventId: ''
    };
  },
  computed: {
    canExportRegistration() {
      return hasPermission('registration:export');
    },
    scopedBrandKeywords() {
      const user = getCurrentUser();
      return Array.isArray(user.brandKeywords)
        ? user.brandKeywords.map(keyword => String(keyword).trim()).filter(Boolean)
        : [];
    },
    hasBrandKeywordScope() {
      return this.scopedBrandKeywords.length > 0;
    },
    isSuperAdmin() {
      const user = getCurrentUser();
      const roleCodes = Array.isArray(user.roleCodes) ? user.roleCodes : [];
      const permissions = Array.isArray(user.permissions) ? user.permissions : [];
      return roleCodes.includes('super_admin') || permissions.includes('*');
    },
    canUseCustomBrandKeyword() {
      return this.isSuperAdmin && !this.hasBrandKeywordScope;
    },
    brandList() {
      if (!this.hasBrandKeywordScope) {
        return REGISTRATION_DASHBOARD_BRANDS;
      }
      const matchedBrands = REGISTRATION_DASHBOARD_BRANDS.filter(brand => {
        return this.scopedBrandKeywords.some(keyword => this.keywordMatchesBrand(keyword, brand));
      });
      const customKeywordBrands = this.scopedBrandKeywords
        .filter(keyword => {
          return !REGISTRATION_DASHBOARD_BRANDS.some(brand => this.keywordMatchesBrand(keyword, brand));
        })
        .map((keyword, index) => ({
          id: `scoped-keyword-${index}`,
          brand: keyword,
          keyword,
          logo: '',
          isScopedKeyword: true
        }));
      return matchedBrands.concat(customKeywordBrands);
    },
    shouldShowBrandSwitcher() {
      return this.hasBrandKeywordScope ? this.brandList.length > 1 : this.brandList.length > 0;
    },
    activeBrand() {
      if (this.isCustomBrandActive) {
        return {
          id: 'custom',
          brand: this.activeCustomKeyword,
          keyword: this.activeCustomKeyword,
          logo: ''
        };
      }
      return this.brandList.find(item => item.id === this.activeBrandId) || this.brandList[0] || {};
    },
    isCustomBrandActive() {
      return this.activeBrandId === 'custom' && !!this.activeCustomKeyword;
    }
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
    this.ensureActiveBrand();
    this.fetchDashboard();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport);
  },
  methods: {
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    normalizeKeyword(value) {
      return String(value || '').trim().toLowerCase();
    },
    keywordMatchesBrand(keyword, brand) {
      const normalizedKeyword = this.normalizeKeyword(keyword);
      if (!normalizedKeyword || !brand) return false;
      return [brand.brand, brand.keyword, brand.id].some(value => {
        const normalizedValue = this.normalizeKeyword(value);
        return normalizedValue &&
          (normalizedValue.includes(normalizedKeyword) || normalizedKeyword.includes(normalizedValue));
      });
    },
    ensureActiveBrand() {
      if (this.isCustomBrandActive && this.canUseCustomBrandKeyword) {
        return;
      }
      if (!this.brandList.length) {
        this.activeBrandId = '';
        this.activeCustomKeyword = '';
        return;
      }
      const exists = this.brandList.some(item => item.id === this.activeBrandId);
      if (!exists || (this.hasBrandKeywordScope && this.activeBrandId === 'custom')) {
        this.activeBrandId = this.brandList[0].id;
        this.activeCustomKeyword = '';
      }
    },
    selectBrand(brandId) {
      if (this.activeBrandId === brandId) {
        this.fetchDashboard();
        return;
      }
      this.activeBrandId = brandId;
      this.activeCustomKeyword = '';
      this.fetchDashboard();
    },
    selectCustomBrand() {
      const keyword = (this.customBrandKeyword || '').trim();
      if (!keyword) {
        this.$message.error('请输入自定义品牌关键词');
        return;
      }
      this.activeBrandId = 'custom';
      this.activeCustomKeyword = keyword;
      this.fetchDashboard();
    },
    clearCustomBrand() {
      this.activeBrandId = this.brandList[0]?.id || '';
      this.activeCustomKeyword = '';
      this.fetchDashboard();
    },
    fetchDashboard() {
      this.ensureActiveBrand();
      if (!this.activeBrandId) return;
      if (this.activeBrandId === 'custom' && !this.activeCustomKeyword) return;
      const brandKeyword = this.hasBrandKeywordScope
        ? this.activeBrand.keyword
        : (this.isCustomBrandActive ? this.activeCustomKeyword : undefined);
      this.loading = true;
      this.$axios
        .get('/form-result/brand_dashboard', {
          params: {
            brandId: this.activeBrandId,
            brandKeyword,
            year: this.year
          },
          headers: {
            'x-auth-token': localStorage.getItem('bde_token')
          }
        })
        .then((res) => {
          this.dashboard = {
            ...emptyDashboard(),
            ...(res.data.res || {})
          };
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '品牌数据大盘加载失败');
          if (err.response?.data?.code === 'token_timeout') {
            this.$router.push('/login');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatMoney(value) {
      return `¥${(Number(value || 0) / 100).toFixed(2)}`;
    },
    formatPercent(value) {
      return `${Number(value || 0).toFixed(1)}%`;
    },
    formatEventDate(event) {
      if (event.fuzzyTime) return event.fuzzyTime;
      const start = event.startTime ? this.$dayjs(event.startTime).format('YYYY-MM-DD') : '';
      const end = event.endTime ? this.$dayjs(event.endTime).format('YYYY-MM-DD') : '';
      if (start && end && start !== end) return `${start} 至 ${end}`;
      return start || end || '--';
    },
    exportEventRegistrations(event) {
      if (!event || !event.eventId) {
        this.$message.error('赛事ID不存在，无法导出');
        return;
      }
      this.exportingEventId = event.eventId;
      const headers = {
        'x-auth-token': localStorage.getItem('bde_token')
      };
      this.$axios
        .get('/form-result/export_excel', {
          params: {
            id: event.eventId,
            validateOnly: true
          },
          headers
        })
        .then(() => {
          return this.$axios.post('/form-result/find_by_id', {
            id: event.eventId,
            onlyPaid: true
          }, {
            headers
          });
        })
        .then((res) => {
          const rows = res.data.res || [];
          if (!rows.length) {
            this.$message.warning('该赛事暂无可导出的报名名单');
            return;
          }
          exportRegistrationRows(rows, {
            eventName: event.eventName,
            eventEndTime: event.endTime
          }, this.$dayjs);
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '导出报名名单失败');
          if (err.response?.data?.code === 'token_timeout') {
            this.$router.push('/login');
          }
        })
        .finally(() => {
          this.exportingEventId = '';
        });
    }
  }
};
</script>

<style scoped>
.registration-brand-dashboard {
  color: var(--text-primary);
}

.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.panel-title h3 {
  margin: 0;
  color: var(--text-primary);
  font-weight: 700;
}

.dashboard-header p,
.panel-title span,
.metric-subtitle {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.dashboard-header p {
  margin-top: 0;
}

.header-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.year-picker {
  width: 120px;
}

.brand-switcher {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.custom-brand-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.custom-brand-input {
  width: 360px;
  max-width: 100%;
}

.brand-tab {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 8px;
  border: 1px solid var(--border-lighter);
  border-radius: 8px;
  background: var(--bg-card);
  color: var(--text-regular);
  cursor: pointer;
  padding: 8px 12px;
}

.brand-tab.active {
  border-color: var(--brand-primary);
  color: var(--brand-primary);
  background: var(--brand-primary-lighter);
}

.brand-tab:hover {
  border-color: var(--brand-primary);
  color: var(--brand-primary);
}

.dark-mode .brand-tab.active {
  border-color: rgba(var(--brand-primary-rgb), 0.9);
  color: var(--brand-primary-light);
  background: rgba(var(--brand-primary-rgb), 0.16);
  box-shadow: inset 0 0 0 1px rgba(var(--brand-primary-rgb), 0.18);
}

.dark-mode .brand-tab.active .brand-logo {
  border-color: rgba(var(--brand-primary-rgb), 0.55);
}

.brand-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  overflow: hidden;
  border: 1px solid var(--border-lighter);
  border-radius: 7px;
  background: #fff;
  color: var(--brand-primary);
  font-size: 12px;
  font-weight: 700;
}

.brand-logo img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.brand-name {
  max-width: 128px;
  overflow: hidden;
  font-size: 13px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.metric-card,
.table-panel {
  border: 1px solid var(--border-lighter);
  border-radius: 8px;
  background: var(--bg-card);
  box-shadow: var(--shadow-light);
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 14px;
  box-sizing: border-box;
  min-height: 118px;
  padding: 20px;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #c04851;
  color: #fff;
  font-size: 24px;
}

.metric-icon-orders {
  background: #2f80ed;
}

.metric-icon-payment {
  background: #16a085;
}

.metric-icon-sharing {
  background: #e6a23c;
}

.metric-icon-ratio {
  background: #6f42c1;
}

.metric-label {
  color: var(--text-regular);
  font-size: 14px;
}

.metric-value {
  margin-top: 6px;
  color: var(--text-primary);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;
}

.table-panel {
  box-sizing: border-box;
  padding: 18px;
}

.panel-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-title h3 {
  font-size: 17px;
}

.brand-event-table {
  border-right: 0;
  border-left: 0;
}

.brand-event-table /deep/ .el-table__border-left-patch,
.brand-event-table /deep/ .el-table__border-right-patch,
.brand-event-table /deep/::before,
.brand-event-table /deep/::after {
  display: none;
}

.brand-event-table /deep/ th,
.brand-event-table /deep/ td {
  border-right: 0;
}

.brand-event-table /deep/ .el-table__cell {
  white-space: nowrap;
}

.brand-event-table /deep/ .event-name-column .cell {
  white-space: normal;
}

.event-name-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.event-name-actions .copy-text {
  min-width: 0;
}

.event-name-actions .el-button {
  flex-shrink: 0;
}

@media (max-width: 1024px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
  }

  .header-tools,
  .year-picker,
  .header-tools > .el-button {
    width: 100%;
  }

  .header-tools {
    flex-direction: column;
    align-items: stretch;
  }

  .custom-brand-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .custom-brand-input {
    width: 100%;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .metric-card,
  .table-panel {
    padding: 14px;
  }

  .metric-card {
    min-height: 104px;
  }

  .metric-value {
    font-size: 24px;
  }

  .brand-event-table /deep/ .event-name-column {
    width: 180px;
    min-width: 180px;
  }

  .brand-event-table /deep/ .event-name-column .cell {
    display: block;
    overflow: visible;
    white-space: normal;
    line-height: 1.35;
  }
}
</style>
